/**
 * Design Specifications for Mocksy
 * Extracted from UXPilot design
 */

export const designSpecs = {
  // Brand
  brand: {
    name: 'Mocksy',
    tagline: 'AI-Powered Magic',
  },

  // Colors
  colors: {
    primary: {
      main: '#6366f1', // Indigo-500
      hover: '#5855eb', // Indigo-600
      light: '#eef2ff', // Indigo-50
      gradient: 'linear-gradient(135deg, #6366f1 0%, #9333ea 100%)', // accent to purple-600
    },
    secondary: {
      main: '#9333ea', // Purple-600
      hover: '#7e22ce', // Purple-700
      light: '#faf5ff', // Purple-50
    },
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
    success: {
      main: '#22c55e', // Green-500
      dark: '#16a34a', // Green-600
      darker: '#15803d', // Green-700
      light: '#dcfce7', // Green-100
    },
    warning: {
      main: '#fbbf24', // Yellow-400
      light: '#fde047', // Yellow-300
    },
    white: '#ffffff',
    black: '#000000',
  },

  // Typography
  typography: {
    fontFamily: {
      primary: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    fontWeights: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    sizes: {
      // Text sizes
      xs: '0.75rem', // 12px
      sm: '0.875rem', // 14px
      base: '1rem', // 16px
      lg: '1.125rem', // 18px
      xl: '1.25rem', // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem', // 48px
      '6xl': '3.75rem', // 60px
      '7xl': '4.5rem', // 72px
    },
  },

  // Spacing
  spacing: {
    section: {
      py: '6rem', // py-24 (96px)
      pyLarge: '9rem', // py-36 (144px)
    },
    container: {
      maxWidth: {
        sm: '640px',
        md: '768px',
        lg: '1024px',
        xl: '1280px',
        '2xl': '1536px',
        '7xl': '80rem', // max-w-7xl
        '4xl': '56rem', // max-w-4xl
      },
    },
  },

  // Border Radius
  borderRadius: {
    sm: '0.375rem', // 6px
    md: '0.5rem', // 8px
    lg: '0.75rem', // 12px - rounded-xl
    xl: '1rem', // 16px - rounded-2xl
    '2xl': '1.5rem', // 24px - rounded-3xl
    full: '9999px',
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    '2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    accent: {
      30: '0 10px 15px -3px rgba(99, 102, 241, 0.3)',
      40: '0 20px 25px -5px rgba(99, 102, 241, 0.4)',
      60: '0 25px 50px -12px rgba(99, 102, 241, 0.6)',
    },
  },

  // Transitions
  transitions: {
    default: 'all 0.3s ease-in-out',
    fast: 'all 0.15s ease-in-out',
    slow: 'all 0.5s ease-in-out',
  },

  // Z-Index
  zIndex: {
    header: 50,
    modal: 100,
    tooltip: 200,
  },

  // Backdrop Blur
  backdropBlur: {
    sm: 'blur(4px)',
    md: 'blur(8px)',
    lg: 'blur(12px)',
    xl: 'blur(16px)',
  },

  // Gradients
  gradients: {
    primary: 'linear-gradient(135deg, #6366f1 0%, #9333ea 100%)',
    background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 50%, #eef2ff 100%)',
    card: 'linear-gradient(135deg, #f9fafb 0%, #eef2ff 100%)',
    accent: 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%)',
  },

  // Component Specific
  components: {
    header: {
      height: '5rem', // h-20 (80px)
      background: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(16px)',
    },
    button: {
      padding: {
        small: '0.625rem 1.5rem', // py-2.5 px-6
        medium: '1rem 2rem', // py-4 px-8
        large: '1.25rem 2rem', // py-5 px-8
      },
    },
    card: {
      padding: '2.5rem', // p-10
      borderWidth: '2px',
    },
    uploadArea: {
      padding: '4rem', // p-16
      borderWidth: '2px',
    },
  },

  // Icon Sizes
  iconSizes: {
    xs: '0.75rem', // 12px
    sm: '1rem', // 16px
    md: '1.25rem', // 20px
    lg: '1.5rem', // 24px
    xl: '2rem', // 32px
    '2xl': '2.25rem', // 36px
  },
}

export default designSpecs
